import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Dropdown, Option } from '../../../../components/shared/nextgen/dropdown';
import SearchInput from '../../../../components/shared/search-input';
import { Lx } from '../../../../i18n/consts';
import MobileFilter from '../../../../components/shared/mobile-dialog';
import styles from './client-filter.module.scss';
import layoutStyles from '../../clients.module.scss';

interface FilterContentProps {
    search: string;
    selectedStatus: number;
    onSearchChange: (value: string) => void;
    onStatusChange: (value: number) => void;
    isMobile: boolean;
}

const FilterContent = ({
    search,
    selectedStatus,
    onSearchChange,
    onStatusChange,
    isMobile,
}: FilterContentProps) => {
    const intl = useIntl();

    const statusOptions = [
        { id: -1, name: intl.formatMessage({ id: Lx.General.ALL_STATUSES }) },
        { id: 0, name: intl.formatMessage({ id: Lx.General.ACTIVE }) },
        { id: 1, name: intl.formatMessage({ id: Lx.General.SUSPENDED }) },
    ];

    return (
        <div className={styles.filterContainer}>
            <SearchInput
                searchValue={search}
                onChange={onSearchChange}
                width={isMobile ? '100%' : '260px'}
            />
            <Dropdown
                name='clientStatusFilter'
                value={selectedStatus}
                placeholder={intl.formatMessage({ id: Lx.General.ALL_STATUSES })}
                onChange={(_, value) => onStatusChange(value as number)}
                selectedTextColor="secondary"
                style={{
                    marginBottom: 0,
                    fontSize: '16px',
                    width: isMobile ? '100%' : '260px',
                }}
            >
                {statusOptions.map(option => (
                    <Option key={option.id} value={option.id}>
                        {option.name}
                    </Option>
                ))}
            </Dropdown>
        </div>
    );
};

export const DEFAULT_CLIENT_FILTER_STATE = {
    search: '',
    selectedStatus: -1,
};

interface ClientFilterProps {
    search: string;
    selectedStatus: number;
    isMobile: boolean;
    mobileView: 'list' | 'filter';
    onFilterChange: (payload: { search: string; selectedStatus: number }) => void;
    onMobileClose: () => void;
    children: React.ReactNode;
    showDesktopFilter: boolean;
    desktopWrapperClassName?: string;
}

export const ClientFilter = ({
    search,
    selectedStatus,
    isMobile,
    mobileView,
    onFilterChange,
    onMobileClose,
    children,
    showDesktopFilter,
    desktopWrapperClassName,
}: ClientFilterProps) => {
    const [pendingSearch, setPendingSearch] = useState(search);
    const [pendingStatus, setPendingStatus] = useState<number>(selectedStatus);

    useEffect(() => {
        if (isMobile && mobileView === 'filter') {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, mobileView, search, selectedStatus]);

    useEffect(() => {
        if (!isMobile) {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, search, selectedStatus]);

    const handleDesktopSearchChange = (value: string) => {
        onFilterChange({ search: value, selectedStatus });
    };

    const handleDesktopStatusChange = (value: number) => {
        onFilterChange({ search, selectedStatus: value });
    };

    const handleMobileApply = () => {
        onFilterChange({ search: pendingSearch, selectedStatus: pendingStatus });
        onMobileClose();
    };

    const handleMobileReset = () => {
        setPendingSearch(DEFAULT_CLIENT_FILTER_STATE.search);
        setPendingStatus(DEFAULT_CLIENT_FILTER_STATE.selectedStatus);
        onFilterChange(DEFAULT_CLIENT_FILTER_STATE);
    };

    if (isMobile && mobileView === 'filter') {
        return (
            <MobileFilter
                onCancel={handleMobileReset}
                onConfirm={handleMobileApply}
                cancelLocalizationId={Lx.General.RESET_FILTER}
            >
                <FilterContent
                    search={pendingSearch}
                    selectedStatus={pendingStatus}
                    onSearchChange={setPendingSearch}
                    onStatusChange={setPendingStatus}
                    isMobile
                />
            </MobileFilter>
        );
    }

    const desktopFilterContent = (
        <FilterContent
            search={search}
            selectedStatus={selectedStatus}
            onSearchChange={handleDesktopSearchChange}
            onStatusChange={handleDesktopStatusChange}
            isMobile={false}
        />
    );

    return (
        <div className={layoutStyles.pageContent}>
            {showDesktopFilter ? (
                <div className={classNames(layoutStyles.desktopFilter, desktopWrapperClassName)}>
                    {desktopFilterContent}
                </div>
            ) : null}
            {children}
        </div>
    );
};

export default ClientFilter;
